import { LiveData, Service } from '@toeverything/infra';

import type { DocService } from '../../doc';
import {
  PRESENCE_GRACE_PERIOD_MS,
  type PresentationMachineState,
  type PresentationSettings,
  StateMachineHelpers,
  type TransitionResult,
  type ViewportState,
} from '../types';
import {
  findResumableOwnedModeration,
  forgetOwnedModerationSession,
  rememberOwnedModerationSession,
} from './owned-moderation-session';
import type {
  PresenterInfo as SocketPresenterInfo,
  SessionState,
  StartModerationResult,
  WorkspaceRealtimeService,
} from './workspace-realtime.service';

/**
 * Storage keys for presentation settings
 */
const STORAGE_KEYS = {
  fillScreen: 'blocksuite:presentation:fillScreen',
  blackBackground: 'blocksuite:presentation:blackBackground',
  hideToolbar: 'blocksuite:presentation:hideToolbar',
} as const;

/**
 * PresentationStateMachine
 *
 * SINGLE SOURCE OF TRUTH for all presentation-related state.
 *
 * SOLID Principles:
 * - Single Responsibility: Manages presentation state transitions only
 * - Open/Closed: New states can be added without modifying existing transitions
 * - Liskov Substitution: All states implement the same interface
 * - Interface Segregation: Exposes only necessary computed values
 * - Dependency Inversion: Depends on abstractions (Awareness), not concretions
 *
 * State Diagram:
 * ```
 *                    ┌─────────┐
 *           ┌───────▶│  IDLE   │◀───────┐
 *           │        └────┬────┘        │
 *           │             │             │
 *      stop │    start    │    start    │ stop
 *           │  moderation │  following  │
 *           │             ▼             │
 *           │   ┌──────────────────┐    │
 *           │   │ MODERATING_FREE  │    │
 *           │   └────────┬─────────┘    │
 *           │            │              │
 *           │     enter  │  exit       │
 *           │     frames │  frames      │
 *           │            ▼              │
 *           │   ┌──────────────────┐    │
 *           └───│ MODERATING_FRAMES│    │
 *               └──────────────────┘    │
 *                                       │
 *               ┌──────────────────┐    │
 *               │ FOLLOWING_FREE   │────┘
 *               └────────┬─────────┘
 *                        │
 *                  join  │  leave
 *                 frames │  frames
 *                        ▼
 *               ┌──────────────────┐
 *               │ FOLLOWING_FRAMES │
 *               └──────────────────┘
 * ```
 *
 * FORBIDDEN TRANSITIONS:
 * - FOLLOWING_* → MODERATING_*  (cannot present while following)
 * - MODERATING_* → FOLLOWING_*  (cannot follow while presenting)
 */
export class PresentationStateMachine extends Service {
  // ═══════════════════════════════════════════════════════════════════════════
  // CORE STATE
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * The single source of truth for presentation state
   */
  readonly state$ = new LiveData<PresentationMachineState>({ type: 'idle' });

  // ═══════════════════════════════════════════════════════════════════════════
  // COMPUTED VALUES (derived from state$, never stored separately)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Whether currently moderating (presenting to others)
   */
  readonly isModerating$ = this.state$.map(StateMachineHelpers.isModerating);

  /**
   * Whether currently following a presenter
   */
  readonly isFollowing$ = this.state$.map(StateMachineHelpers.isFollowing);

  /**
   * Whether in frame-based presentation mode
   */
  readonly isInFrameMode$ = this.state$.map(StateMachineHelpers.isInFrameMode);

  /**
   * Current frame index (null if not in frame mode)
   */
  readonly frameIndex$ = this.state$.map(StateMachineHelpers.getFrameIndex);

  /**
   * Current presentation mode ('free' | 'frames' | null)
   */
  readonly currentMode$ = this.state$.map(s => {
    if (s.type === 'moderating_free' || s.type === 'following_free')
      return 'free';
    if (s.type === 'moderating_frames' || s.type === 'following_frames')
      return 'frames';
    return null;
  });

  /**
   * Presenter ID we're following (null if not following)
   */
  readonly presenterId$ = this.state$.map(StateMachineHelpers.getPresenterId);

  /**
   * Whether follower has joined the frame presentation (fullscreen view)
   */
  readonly isFollowerJoined$ = this.state$.map(s =>
    s.type === 'following_frames' ? s.isJoined : false
  );

  /**
   * Reconnection state for UI feedback
   */
  readonly isReconnecting$ = new LiveData<boolean>(false);

  /**
   * Request to join presentation at specific frame (UI → Editor)
   * Editor.ts subscribes to this and triggers unified activation code path.
   */
  readonly joinPresentationRequest$ = new LiveData<{
    frameIndex: number;
    frameId: string;
  } | null>(null);

  /**
   * Request to join presentation - triggers unified code path in Editor.ts
   */
  requestJoinPresentation(
    frameIndex: number,
    frameId: string
  ): TransitionResult {
    console.log('[StateMachine] requestJoinPresentation:', frameIndex);

    const transition = this.joinFrameMode(frameIndex, frameId);
    if (!transition.success) {
      return transition;
    }

    this.joinPresentationRequest$.next({ frameIndex, frameId });
    return transition;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PRIVATE STATE (not exposed, internal bookkeeping only)
  // ═══════════════════════════════════════════════════════════════════════════

  private disconnectTimeoutId?: ReturnType<typeof setTimeout>;
  private socketSubscription?: { unsubscribe: () => void };
  private isResumingOwnedModeration = false;

  constructor(
    private readonly docService: DocService,
    private readonly socketService: WorkspaceRealtimeService
  ) {
    super();
    this.setupSocketSubscriptions();
  }

  /**
   * Get the socket service for server communication
   * Used by other services that need to interact with presentation backend
   */
  get socket(): WorkspaceRealtimeService {
    return this.socketService;
  }

  /**
   * Subscribe to socket events for state transitions
   * sl-ck8v: Server-mediated state updates
   */
  private setupSocketSubscriptions(): void {
    // Subscribe to connection status (for disconnect handling)
    const connectionSub = this.socketService.isConnected$.subscribe(
      isConnected => {
        if (!StateMachineHelpers.isFollowing(this.state$.value)) return;

        if (!isConnected) {
          // Connection lost while following - start grace period
          this.handlePresenterMissing();
        } else {
          // Reconnected
          this.handlePresenterReconnected();
        }
      }
    );

    // Subscribe to presenter state updates (for followers)
    const presenterSub = this.socketService.sessionState$.subscribe(session => {
      if (
        session &&
        this.state$.value.type === 'idle' &&
        this.socketService.isFollowing$.value &&
        session.docId === this.docService.doc.id
      ) {
        this.resumeFollowingSession(session);
      }

      if (!session || !StateMachineHelpers.isFollowing(this.state$.value))
        return;

      // Handle mode changes from presenter
      const currentState = this.state$.value;
      if (session.mode === 'frames' && currentState.type === 'following_free') {
        console.log(
          '[StateMachine] Presenter switched to frames, entering frame mode'
        );
        if (session.frameIndex !== undefined && session.frameId !== undefined) {
          this.enterFrameModeAsFollower(session.frameIndex, session.frameId);
        } else {
          console.warn(
            '[StateMachine] Missing frameIndex or frameId in session'
          );
        }
      } else if (
        session.mode === 'free' &&
        currentState.type === 'following_frames'
      ) {
        console.log(
          '[StateMachine] Presenter switched to free, leaving frame mode'
        );
        this.leaveFrameMode();
      }
    });

    // Subscribe to presenter viewport updates
    const viewportSub = this.socketService.presenterViewport$.subscribe(() => {
      // Viewport updates are handled by FollowerViewportSyncService
      // Just ensure we're still following
      if (!StateMachineHelpers.isFollowing(this.state$.value)) return;
    });

    // Subscribe to presenter frame updates
    const frameSub = this.socketService.presenterFrame$.subscribe(frame => {
      if (!frame || !StateMachineHelpers.isFollowing(this.state$.value)) return;
      this.syncFrameFromPresenter(frame.frameIndex, frame.frameId);
    });

    // Subscribe to presenter stopped event
    const stoppedSub = this.socketService.isFollowing$.subscribe(
      isFollowing => {
        if (
          !isFollowing &&
          StateMachineHelpers.isFollowing(this.state$.value)
        ) {
          // Presenter stopped, return to idle
          console.log(
            '[StateMachine] Presenter stopped (via socket), returning to idle'
          );
          this.state$.next({ type: 'idle' });
        }
      }
    );

    const moderationStoppedSub = this.socketService.isModerating$.subscribe(
      isModerating => {
        if (
          !isModerating &&
          StateMachineHelpers.isModerating(this.state$.value)
        ) {
          console.log(
            '[StateMachine] Moderation session no longer active, returning to idle'
          );
          this.state$.next({ type: 'idle' });
        }
      }
    );

    const followFailedSub = this.socketService.followFailed$.subscribe(
      event => {
        if (!event || event.docId !== this.docService.doc.id) return;
        if (!StateMachineHelpers.isFollowing(this.state$.value)) return;

        console.warn(
          '[StateMachine] Follow request failed, returning to idle',
          event.error
        );
        this.state$.next({ type: 'idle' });
      }
    );

    const activeModerationsSub =
      this.socketService.activeModerations$.subscribe(moderations => {
        this.resumeOwnedModerationFromActiveList(moderations).catch(error => {
          console.warn(
            '[StateMachine] Failed to resume owned moderation',
            error
          );
        });
      });

    this.socketSubscription = {
      unsubscribe: () => {
        connectionSub.unsubscribe();
        presenterSub.unsubscribe();
        viewportSub.unsubscribe();
        frameSub.unsubscribe();
        stoppedSub.unsubscribe();
        moderationStoppedSub.unsubscribe();
        followFailedSub.unsubscribe();
        activeModerationsSub.unsubscribe();
      },
    };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // MODERATOR TRANSITIONS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Start moderation (presentation mode)
   * Transition: IDLE → MODERATING_FREE
   *
   * sl-ck8v: Now uses Socket for server communication
   */
  async startModeration(): Promise<TransitionResult> {
    const current = this.state$.value;

    // GUARD: Only from IDLE
    if (current.type !== 'idle') {
      return this.failedTransition(
        current,
        `Cannot start moderation from state: ${current.type}`
      );
    }

    const settings = this.getSettingsFromStorage();

    this.isResumingOwnedModeration = true;
    let result: StartModerationResult;
    try {
      // Ensure socket is connected (sl-jmyl.6: socket is workspace-scoped, already connected via Editor)
      const docId = this.docService.doc.id;
      await this.socketService.setActiveDoc(docId);

      // Start moderation on server
      result = await this.socketService.startModeration('free', settings);
      if (!result.success) {
        return this.failedTransition(
          current,
          result.error ?? 'Failed to start moderation'
        );
      }
    } finally {
      this.isResumingOwnedModeration = false;
    }

    const newState = this.createModeratingStateFromSession(result, settings);

    if (result.sessionId) {
      rememberOwnedModerationSession(result.sessionId);
    }
    this.state$.next(newState);

    console.log('[StateMachine] Transition: IDLE → MODERATING_FREE');
    return this.successTransition(current, newState);
  }

  /**
   * Stop moderation
   * Transition: MODERATING_* → IDLE
   *
   * sl-ck8v: Now uses Socket for server communication
   */
  async stopModeration(): Promise<TransitionResult> {
    const current = this.state$.value;

    // GUARD: Only from MODERATING states
    if (!StateMachineHelpers.isModerating(current)) {
      return this.failedTransition(
        current,
        `Cannot stop moderation from state: ${current.type}`
      );
    }

    // Stop on server
    await this.socketService.stopModeration();
    forgetOwnedModerationSession();

    const newState: PresentationMachineState = { type: 'idle' };
    this.state$.next(newState);

    console.log('[StateMachine] Transition:', current.type, '→ IDLE');
    return this.successTransition(current, newState);
  }

  /**
   * Toggle moderation on/off
   *
   * sl-ck8v: Now async due to socket communication
   */
  async toggleModeration(): Promise<TransitionResult> {
    const current = this.state$.value;
    if (StateMachineHelpers.isModerating(current)) {
      return this.stopModeration();
    } else {
      return this.startModeration();
    }
  }

  /**
   * Enter frame mode (when PresentTool is activated)
   * Transition: MODERATING_FREE → MODERATING_FRAMES
   *
   * sl-ck8v: Now uses Socket for server communication
   */
  enterFrameMode(
    frameIndex: number,
    frameId: string,
    totalFrames: number
  ): TransitionResult {
    const current = this.state$.value;

    // GUARD: Only from MODERATING_FREE
    if (current.type !== 'moderating_free') {
      return this.failedTransition(
        current,
        `Cannot enter frame mode from state: ${current.type}`
      );
    }

    // GUARD: Valid frame index
    if (frameIndex < 0 || frameIndex >= totalFrames) {
      return this.failedTransition(
        current,
        `Invalid frame index: ${frameIndex} (total: ${totalFrames})`
      );
    }

    const newState: PresentationMachineState = {
      type: 'moderating_frames',
      frameIndex,
      frameId,
      totalFrames,
      settings: current.settings,
    };

    this.state$.next(newState);

    // Update server via socket
    this.socketService.setMode('frames', frameIndex, frameId);

    console.log(
      '[StateMachine] Transition: MODERATING_FREE → MODERATING_FRAMES, frame:',
      frameIndex
    );
    return this.successTransition(current, newState);
  }

  /**
   * Exit frame mode (when PresentTool is deactivated)
   * Transition: MODERATING_FRAMES → MODERATING_FREE
   *
   * sl-ck8v: Now uses Socket for server communication
   */
  exitFrameMode(): TransitionResult {
    const current = this.state$.value;

    // GUARD: Only from MODERATING_FRAMES
    if (current.type !== 'moderating_frames') {
      return this.failedTransition(
        current,
        `Cannot exit frame mode from state: ${current.type}`
      );
    }

    const newState: PresentationMachineState = {
      type: 'moderating_free',
      settings: current.settings,
    };

    this.state$.next(newState);

    // Update server via socket
    this.socketService.setMode('free');

    console.log(
      '[StateMachine] Transition: MODERATING_FRAMES → MODERATING_FREE'
    );
    return this.successTransition(current, newState);
  }

  /**
   * Navigate to a specific frame (while in frame mode)
   * Updates state without transition (same state type)
   *
   * sl-ck8v: Now uses Socket for server communication
   */
  setFrameIndex(frameIndex: number, frameId: string): TransitionResult {
    const current = this.state$.value;

    // GUARD: Only in MODERATING_FRAMES
    if (current.type !== 'moderating_frames') {
      return this.failedTransition(
        current,
        `Cannot set frame index in state: ${current.type}`
      );
    }

    // GUARD: Valid frame index
    if (frameIndex < 0 || frameIndex >= current.totalFrames) {
      return this.failedTransition(
        current,
        `Invalid frame index: ${frameIndex} (total: ${current.totalFrames})`
      );
    }

    // No change?
    if (current.frameIndex === frameIndex && current.frameId === frameId) {
      return this.successTransition(current, current);
    }

    const newState: PresentationMachineState = {
      ...current,
      frameIndex,
      frameId,
    };

    this.state$.next(newState);

    // Broadcast via socket
    this.socketService.setFrame(frameIndex, frameId);

    console.log(
      '[StateMachine] Frame change:',
      current.frameIndex,
      '→',
      frameIndex
    );
    return this.successTransition(current, newState);
  }

  /**
   * Broadcast viewport (free mode only)
   *
   * sl-ck8v: Now uses Socket for server communication
   */
  broadcastViewport(viewport: ViewportState): void {
    const current = this.state$.value;

    // GUARD: Only in MODERATING_FREE
    if (current.type !== 'moderating_free') {
      return;
    }

    this.socketService.broadcastViewport(viewport);
  }

  /**
   * Update presentation settings
   *
   * sl-ck8v: Now uses Socket for server communication
   */
  updateSettings(settings: Partial<PresentationSettings>): TransitionResult {
    const current = this.state$.value;

    // GUARD: Only in MODERATING states
    if (!StateMachineHelpers.isModerating(current)) {
      return this.failedTransition(
        current,
        `Cannot update settings in state: ${current.type}`
      );
    }

    const newSettings: PresentationSettings = {
      ...current.settings,
      ...settings,
    };

    const newState: PresentationMachineState = {
      ...current,
      settings: newSettings,
    };

    this.state$.next(newState);

    // Update via socket
    this.socketService.updateSettings(newSettings);

    return this.successTransition(current, newState);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // FOLLOWER TRANSITIONS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Start following a presenter
   * Transition: IDLE → FOLLOWING_FREE or FOLLOWING_FRAMES (depending on presenter mode)
   *
   * sl-ck8v: Now uses Socket for server communication
   * The actual state transition happens when socket:follow-success event is received
   */
  async startFollowing(
    moderatorClientId: string,
    presenterName?: string
  ): Promise<TransitionResult> {
    const current = this.state$.value;

    // GUARD: Only from IDLE
    if (current.type !== 'idle') {
      return this.failedTransition(
        current,
        `Cannot start following from state: ${current.type}`
      );
    }

    // Ensure socket is connected (sl-jmyl.6: socket is workspace-scoped, already connected via Editor)
    const docId = this.docService.doc.id;
    await this.socketService.setActiveDoc(docId);

    // Follow via socket - this will trigger follow-success event
    await this.socketService.follow(moderatorClientId);

    // Wait for socket response via subscription
    // The actual state transition happens in setupSocketSubscriptions()
    // For now, we optimistically transition to following_free
    const newState: PresentationMachineState = {
      type: 'following_free',
      presenterId: moderatorClientId,
      presenterName,
    };

    this.state$.next(newState);

    console.log(
      '[StateMachine] Transition: IDLE → FOLLOWING_FREE, waiting for server confirmation'
    );
    return this.successTransition(current, newState);
  }

  /**
   * Stop following
   * Transition: FOLLOWING_* → IDLE
   *
   * sl-ck8v: Now uses Socket for server communication
   */
  async stopFollowing(): Promise<TransitionResult> {
    const current = this.state$.value;

    // GUARD: Only from FOLLOWING states
    if (!StateMachineHelpers.isFollowing(current)) {
      return this.failedTransition(
        current,
        `Cannot stop following from state: ${current.type}`
      );
    }

    // Clear reconnection state
    this.clearReconnectionState();

    // Unfollow via socket
    await this.socketService.unfollow();

    const newState: PresentationMachineState = { type: 'idle' };
    this.state$.next(newState);

    console.log('[StateMachine] Transition:', current.type, '→ IDLE');
    return this.successTransition(current, newState);
  }

  /**
   * Enter frame mode as follower (NOT joined yet, waiting for fullscreen)
   * Transition: FOLLOWING_FREE → FOLLOWING_FRAMES (isJoined: false)
   *
   * Called automatically when presenter enters frame mode.
   * User sees fullscreen prompt overlay.
   */
  enterFrameModeAsFollower(
    frameIndex: number,
    frameId: string
  ): TransitionResult {
    const current = this.state$.value;

    // GUARD: Only from FOLLOWING_FREE
    if (current.type !== 'following_free') {
      return this.failedTransition(
        current,
        `Cannot enter frame mode from state: ${current.type}`
      );
    }

    const newState: PresentationMachineState = {
      type: 'following_frames',
      presenterId: current.presenterId,
      presenterName: current.presenterName,
      frameIndex,
      frameId,
      isJoined: false, // NOT joined yet - waiting for fullscreen
    };

    this.state$.next(newState);

    console.log(
      '[StateMachine] Transition: FOLLOWING_FREE → FOLLOWING_FRAMES (not joined), frame:',
      frameIndex
    );
    return this.successTransition(current, newState);
  }

  /**
   * Join frame presentation (enter fullscreen view as follower)
   * Transition: FOLLOWING_FRAMES (not joined) → FOLLOWING_FRAMES (joined)
   *
   * Called when user clicks to enter fullscreen.
   */
  joinFrameMode(frameIndex: number, frameId: string): TransitionResult {
    const current = this.state$.value;

    // GUARD: Only from FOLLOWING_FREE or FOLLOWING_FRAMES (not joined)
    if (
      current.type !== 'following_free' &&
      current.type !== 'following_frames'
    ) {
      return this.failedTransition(
        current,
        `Cannot join frame mode from state: ${current.type}`
      );
    }

    const newState: PresentationMachineState = {
      type: 'following_frames',
      presenterId: current.presenterId,
      presenterName: current.presenterName,
      frameIndex,
      frameId,
      isJoined: true,
    };

    this.state$.next(newState);

    console.log(
      '[StateMachine] Transition:',
      current.type,
      '→ FOLLOWING_FRAMES (joined), frame:',
      frameIndex
    );
    return this.successTransition(current, newState);
  }

  /** Keep following frames after leaving the fullscreen presentation view. */
  leaveFullscreenView(): TransitionResult {
    const current = this.state$.value;
    if (current.type !== 'following_frames') {
      return this.failedTransition(
        current,
        `Cannot leave fullscreen view from state: ${current.type}`
      );
    }

    const newState: PresentationMachineState = {
      ...current,
      isJoined: false,
    };
    this.state$.next(newState);
    return this.successTransition(current, newState);
  }

  /**
   * Leave frame presentation (exit fullscreen view as follower)
   * Transition: FOLLOWING_FRAMES → FOLLOWING_FREE
   */
  leaveFrameMode(): TransitionResult {
    const current = this.state$.value;

    // GUARD: Only from FOLLOWING_FRAMES
    if (current.type !== 'following_frames') {
      return this.failedTransition(
        current,
        `Cannot leave frame mode from state: ${current.type}`
      );
    }

    const newState: PresentationMachineState = {
      type: 'following_free',
      presenterId: current.presenterId,
      presenterName: current.presenterName,
    };

    this.state$.next(newState);

    console.log('[StateMachine] Transition: FOLLOWING_FRAMES → FOLLOWING_FREE');
    return this.successTransition(current, newState);
  }

  /**
   * Sync frame index from presenter (while following in frame mode)
   * Does NOT change state type, only updates frame within FOLLOWING_FRAMES
   */
  syncFrameFromPresenter(frameIndex: number, frameId: string): void {
    const current = this.state$.value;

    // Only update if in FOLLOWING_FRAMES
    if (current.type !== 'following_frames') {
      return;
    }

    // No change?
    if (current.frameIndex === frameIndex && current.frameId === frameId) {
      return;
    }

    this.state$.next({
      ...current,
      frameIndex,
      frameId,
    });

    console.log('[StateMachine] Synced frame from presenter:', frameIndex);
  }

  /**
   * Handle presenter mode change (free ↔ frames)
   * Called when presenter switches modes while we're following
   */
  handlePresenterModeChange(
    newMode: 'free' | 'frames',
    frameIndex?: number,
    frameId?: string
  ): void {
    const current = this.state$.value;

    console.log('[StateMachine] handlePresenterModeChange:', {
      newMode,
      frameIndex,
      frameId,
      currentType: current.type,
    });

    if (!StateMachineHelpers.isFollowing(current)) {
      console.log('[StateMachine] Not following, ignoring mode change');
      return;
    }

    if (newMode === 'frames' && current.type === 'following_free') {
      // Presenter switched to frames → automatically enter frame mode (not joined)
      // User will see fullscreen prompt overlay
      if (frameIndex !== undefined && frameId !== undefined) {
        console.log(
          '[StateMachine] Presenter switched to frames mode, entering frame mode with frame:',
          frameIndex
        );
        this.enterFrameModeAsFollower(frameIndex, frameId);
      } else {
        console.log(
          '[StateMachine] Missing frameIndex or frameId, cannot enter frame mode'
        );
      }
    } else if (newMode === 'free' && current.type === 'following_frames') {
      // Presenter switched to free mode, exit frame view
      console.log(
        '[StateMachine] Presenter switched to free mode, leaving frame mode'
      );
      this.leaveFrameMode();
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PRESENTER DISCOVERY (sl-ck8v: Now via Socket Service)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Get all available presenters (via Socket Service)
   *
   * sl-ck8v: Now uses server-side session registry instead of Y.js Awareness
   * Returns SocketPresenterInfo[] (not legacy PresenterInfo from types.ts)
   */
  getAvailablePresenters(): SocketPresenterInfo[] {
    return this.socketService.availablePresenters$.value;
  }

  /**
   * Get presenter's current viewport (for followers)
   *
   * sl-ck8v: Now uses Socket Service LiveData
   */
  getPresenterViewport(): ViewportState | null {
    const current = this.state$.value;

    if (!StateMachineHelpers.isFollowing(current)) {
      return null;
    }

    return this.socketService.presenterViewport$.value;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PRIVATE HELPERS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Handle presenter missing (possible disconnect)
   * sl-ck8v: Now triggered by socket disconnect events
   */
  private handlePresenterMissing(): void {
    if (this.isReconnecting$.value) return;

    this.isReconnecting$.next(true);

    this.disconnectTimeoutId = setTimeout(() => {
      if (this.isReconnecting$.value) {
        console.log(
          '[StateMachine] Presenter not reconnected, stopping following'
        );
        this.stopFollowing().catch(console.error);
      }
    }, PRESENCE_GRACE_PERIOD_MS);
  }

  /**
   * Handle presenter reconnected
   */
  private handlePresenterReconnected(): void {
    if (!this.isReconnecting$.value) return;

    this.clearReconnectionState();
    console.log('[StateMachine] Presenter reconnected');
  }

  private async resumeOwnedModerationFromActiveList(
    moderations: SocketPresenterInfo[]
  ): Promise<void> {
    if (this.isResumingOwnedModeration || this.state$.value.type !== 'idle') {
      return;
    }

    const currentUserId = this.socketService.getCurrentUserId();
    if (!currentUserId) {
      return;
    }

    const ownedModeration = findResumableOwnedModeration(
      moderations,
      this.docService.doc.id,
      currentUserId
    );
    if (!ownedModeration) {
      return;
    }

    this.isResumingOwnedModeration = true;
    try {
      const settings =
        ownedModeration.settings ?? this.getSettingsFromStorage();
      const result = await this.socketService.startModeration(
        ownedModeration.mode,
        settings,
        ownedModeration.frameIndex,
        ownedModeration.frameId
      );

      if (!result.success || this.state$.value.type !== 'idle') {
        return;
      }

      const newState = this.createModeratingStateFromSession(result, settings);
      this.state$.next(newState);
      console.log(
        '[StateMachine] Resumed owned moderation session after reconnect'
      );
    } finally {
      this.isResumingOwnedModeration = false;
    }
  }

  private resumeFollowingSession(session: SessionState): void {
    const followerIdentity = {
      presenterId: session.moderatorClientId,
      presenterName: session.moderatorName,
    };

    if (
      session.mode === 'frames' &&
      session.frameIndex !== undefined &&
      session.frameId !== undefined
    ) {
      this.state$.next({
        type: 'following_frames',
        ...followerIdentity,
        frameIndex: session.frameIndex,
        frameId: session.frameId,
        isJoined: false,
      });
      return;
    }

    this.state$.next({
      type: 'following_free',
      ...followerIdentity,
    });
  }

  private createModeratingStateFromSession(
    session: StartModerationResult,
    fallbackSettings: PresentationSettings
  ): Extract<
    PresentationMachineState,
    { type: 'moderating_free' | 'moderating_frames' }
  > {
    const settings = session.settings ?? fallbackSettings;
    if (
      session.mode === 'frames' &&
      session.frameIndex !== undefined &&
      session.frameId !== undefined
    ) {
      return {
        type: 'moderating_frames',
        frameIndex: session.frameIndex,
        frameId: session.frameId,
        totalFrames: Math.max(session.frameIndex + 1, 1),
        settings,
      };
    }

    return {
      type: 'moderating_free',
      settings,
    };
  }

  /**
   * Clear reconnection state
   */
  private clearReconnectionState(): void {
    if (this.disconnectTimeoutId) {
      clearTimeout(this.disconnectTimeoutId);
      this.disconnectTimeoutId = undefined;
    }
    this.isReconnecting$.next(false);
  }

  /**
   * Get settings from localStorage
   */
  private getSettingsFromStorage(): PresentationSettings {
    return {
      fillScreen: this.getStorageBoolean(STORAGE_KEYS.fillScreen, false),
      blackBackground: this.getStorageBoolean(
        STORAGE_KEYS.blackBackground,
        true
      ),
      hideToolbar: this.getStorageBoolean(STORAGE_KEYS.hideToolbar, false),
    };
  }

  /**
   * Get boolean from localStorage
   */
  private getStorageBoolean(key: string, defaultValue: boolean): boolean {
    try {
      const value = localStorage.getItem(key);
      if (value === null) return defaultValue;
      return JSON.parse(value) === true;
    } catch {
      return defaultValue;
    }
  }

  /**
   * Create successful transition result
   */
  private successTransition(
    previousState: PresentationMachineState,
    newState: PresentationMachineState
  ): TransitionResult {
    return { success: true, previousState, newState };
  }

  /**
   * Create failed transition result
   */
  private failedTransition(
    currentState: PresentationMachineState,
    error: string
  ): TransitionResult {
    console.warn('[StateMachine] Transition failed:', error);
    return {
      success: false,
      previousState: currentState,
      newState: currentState,
      error,
    };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // LIFECYCLE
  // ═══════════════════════════════════════════════════════════════════════════

  override dispose(): void {
    this.clearReconnectionState();
    if (this.socketSubscription) {
      this.socketSubscription.unsubscribe();
    }
    super.dispose();
  }
}
